"use client";

import { useEffect, type ReactNode } from "react";
import { useReiterSteuerung, useScrollHinweis, type Maske } from "./reiter";

/** Lucide „Check“ und „Lock“ als Inline-SVG (das Paket bindet lucide-react nicht ein, siehe `zeilen-symbole.tsx`). */
function Haken({ klasse }: { klasse: string }) {
  return (
    <svg className={klasse} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function Schloss({ klasse }: { klasse: string }) {
  return (
    <svg className={klasse} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/**
 * Phasen-Leiste (Design-Guide `phasen-leiste.md`, Besprechung-Phasen, Betreiber 19.10.2026 „H + T2“): die Phasen EINES Objekts als waagerechte Leiste mit Kreisen und
 * einer Linie dazwischen, die zugleich die Reiter des Objekts sind (ersetzt die unterstrichenen Reiter, wo ein Objekt einen Weg hat: Vorbereiten, Durchführen,
 * Nachbereiten, Veröffentlicht). Verhalten bei Tastatur und ARIA wie `Reiter` (`useReiterSteuerung`: tablist/tab/tabpanel, Pfeile, Pos1, Ende, roving tabindex,
 * `aria-controls`); nicht gewählte Panels bleiben im Markup (hidden).
 *
 * Zustand je Station (`zustand`, vom Aufrufer; die Leiste rechnet nichts aus dem Objekt): abgeschlossen = gefüllter Kreis mit Haken, aktuell = Symbol im Ring (hervorgehoben,
 * `aria-current="step"`), künftig = leerer Kreis mit dem Symbol der Phase. Gesperrt (`gesperrt`) ist unabhängig davon: Schloss im gestrichelten Kreis, sichtbar, nicht wählbar
 * (`aria-disabled`), der Zusatz sagt den Grund („noch nicht veröffentlicht“), der Tooltip (`sperrHinweis`) den Ausweg. Die Linie ist bis zur aktuellen Station durchgezogen,
 * danach gestrichelt. Gewählt (nicht dasselbe wie aktuell: man blättert zurück) ist die Station mit Unterstrich und fetter Schrift, `aria-selected`.
 * Der Zustand ist nie nur Farbe: Haken, Ring, Schloss und gestrichelte Form tragen ihn, dazu steht er als Text für Hilfstechnik (sr-only).
 *
 * Der Baustein kennt keine Rechte und keine Daten: Zustand, Zusatz und Sperre kommen vom Aufrufer. Mobil (unter `sm`) schmalere Stationen, was trotzdem überläuft, scrollt
 * waagerecht mit Rand-Verlauf (wie `Reiter`).
 *
 * Abweichung AW-122: Eigenbau; der Katalogblock flow-header-with-step-timeline (Stationen mit Linie) hat `dark:`, Inline-Variable, Roh-Palette, Logo und Link, keine Zustände
 * und keinen Zusatztext; nur die Idee der Linie zwischen den Stationen ist übernommen.
 */
export type PhasenZustand = "abgeschlossen" | "aktuell" | "kuenftig";

export interface PhasenStation {
  id: string;
  label: string;
  /** Symbol der Phase (Lucide, `aria-hidden` setzt der Baustein); steht im Kreis, solange die Station nicht abgeschlossen oder gesperrt ist. */
  symbol: ReactNode;
  zustand: PhasenZustand;
  /** Kurzer Zusatz unter dem Namen („4 Punkte“, „läuft“, „noch nicht veröffentlicht“); steht im Namen der Station. */
  zusatz?: string;
  /** Sichtbar, aber nicht wählbar (Schloss, gestrichelter Kreis). */
  gesperrt?: boolean;
  /** Tooltip der gesperrten Station („Erscheint nach dem Veröffentlichen“). */
  sperrHinweis?: string;
  inhalt: ReactNode;
}

/** Wortquelle für Hilfstechnik (sr-only): der Zustand steht nie nur als Form. */
export const PHASEN_SR: Record<PhasenZustand, string> = {
  abgeschlossen: "abgeschlossen",
  aktuell: "aktuelle Phase",
  kuenftig: "noch nicht erreicht",
};
export const PHASEN_SR_GESPERRT = "gesperrt";

const KREIS = "relative z-10 inline-flex size-10 shrink-0 items-center justify-center rounded-full border-2";
const KREIS_KLASSE: Record<PhasenZustand, string> = {
  abgeschlossen: `${KREIS} bg-primary border-primary text-primary-foreground`,
  aktuell: `${KREIS} bg-layer border-primary text-primary ring-4 ring-primary-200`,
  kuenftig: `${KREIS} bg-layer border-line-3 text-muted-foreground-1`,
};
const KREIS_GESPERRT = `${KREIS} bg-layer border-dashed border-line-3 text-muted-foreground-1`;
const STATION =
  "relative z-10 w-full px-1 py-1 flex flex-col items-center gap-y-1 text-center rounded-lg focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
/** Feste, vollständige Klassenketten (nie zusammengesetzt) für die Linie vor einer Station. */
const LINIE_DURCH = "absolute top-6 -inset-s-1/2 w-full border-t-2 border-primary pointer-events-none";
const LINIE_GESTRICHELT = "absolute top-6 -inset-s-1/2 w-full border-t-2 border-dashed border-line-3 pointer-events-none";
const LEISTE: Record<Maske, string> = {
  keine: "overflow-x-auto",
  rechts: "overflow-x-auto mask-r-from-90%",
  links: "overflow-x-auto mask-l-from-90%",
  beide: "overflow-x-auto mask-x-from-90%",
};

export function PhasenLeiste({
  beschriftung,
  stationen,
  start,
  aktiv: aktivVorgabe,
  beimWechsel,
}: {
  beschriftung: string;
  stationen: PhasenStation[];
  /** Id der anfangs gewählten Station (Standard: die erste). */
  start?: string;
  /** Gesteuerter Betrieb wie bei `Reiter`. */
  aktiv?: string;
  /** `durchTastatur`: der Wechsel kam von den Pfeil-, Pos1- oder Ende-Tasten. */
  beimWechsel?: (id: string, durchTastatur: boolean) => void;
}) {
  const { aktiv, knoepfe, tabId, panelId, knopfRef, waehle, taste } = useReiterSteuerung({
    eintraege: stationen,
    ...(start !== undefined ? { start } : {}),
    ...(aktivVorgabe !== undefined ? { aktiv: aktivVorgabe } : {}),
    ...(beimWechsel ? { beimWechsel } : {}),
  });
  const { ref: scrollRef, maske } = useScrollHinweis();
  // Die gewählte Station liegt immer im sichtbaren Teil der quer laufenden Leiste (nur waagerecht, die Seite springt nicht).
  useEffect(() => {
    const leiste = scrollRef.current;
    const knopf = knoepfe.current.get(aktiv);
    if (!leiste || !knopf) return;
    const k = knopf.getBoundingClientRect();
    const l = leiste.getBoundingClientRect();
    if (k.left < l.left) leiste.scrollLeft -= l.left - k.left + 8;
    else if (k.right > l.right) leiste.scrollLeft += k.right - l.right + 8;
  }, [aktiv, scrollRef, knoepfe]);

  return (
    <div>
      {/* Preline: navigation-and-layout/navigation-and-layout-sub-headers-navigation/navigation-and-layout-cat-flow-headers/flow-header-with-step-timeline */}
      {/* Abweichung AW-122: nur die Linie zwischen den Stationen ist übernommen, der Rest ist Eigenbau */}
      <div ref={scrollRef} className={LEISTE[maske]}>
        <div className="flex pb-1" role="tablist" aria-label={beschriftung} aria-orientation="horizontal">
          {stationen.map((s, i) => {
            const gewaehlt = aktiv === s.id;
            const vorher = i > 0 ? stationen[i - 1] : undefined;
            // Durchgezogen von einer abgeschlossenen Station bis zur aktuellen (oder zur nächsten abgeschlossenen), sonst gestrichelt.
            const durch = vorher !== undefined && vorher.zustand === "abgeschlossen" && s.zustand !== "kuenftig";
            return (
              <div key={s.id} role="presentation" className="relative flex-1 min-w-20 sm:min-w-28">
                {vorher && <span aria-hidden="true" className={durch ? LINIE_DURCH : LINIE_GESTRICHELT} data-linie={durch ? "durch" : "gestrichelt"} />}
                <button
                  ref={knopfRef(s.id)}
                  type="button"
                  role="tab"
                  id={tabId(s.id)}
                  aria-selected={gewaehlt}
                  aria-controls={panelId(s.id)}
                  aria-disabled={s.gesperrt || undefined}
                  aria-current={s.zustand === "aktuell" && !s.gesperrt ? "step" : undefined}
                  tabIndex={gewaehlt ? 0 : -1}
                  title={s.gesperrt ? s.sperrHinweis : undefined}
                  data-zustand={s.gesperrt ? "gesperrt" : s.zustand}
                  className={STATION}
                  onClick={() => waehle(s.id, false)}
                  onKeyDown={(ev) => taste(ev, i)}
                >
                  <span className={s.gesperrt ? KREIS_GESPERRT : KREIS_KLASSE[s.zustand]} aria-hidden="true">
                    {s.gesperrt ? <Schloss klasse="size-4" /> : s.zustand === "abgeschlossen" ? <Haken klasse="size-5" /> :<span className="inline-flex size-5 items-center justify-center">{s.symbol}</span>}
                  </span>
                  <span className={gewaehlt ? "text-sm font-semibold text-foreground underline decoration-2 decoration-primary underline-offset-4" : s.zustand === "aktuell" && !s.gesperrt ? "text-sm font-semibold text-foreground" : "text-sm text-muted-foreground-1"}>
                    {s.label}
                    <span className="sr-only">{`, ${s.gesperrt ? PHASEN_SR_GESPERRT : PHASEN_SR[s.zustand]}`}</span>
                  </span>
                  {s.zusatz && (
                    <>
                      {" "}
                      <span className="text-xs text-muted-foreground-1">{s.zusatz}</span>
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {stationen.map((s) => (
        <div key={s.id} role="tabpanel" id={panelId(s.id)} aria-labelledby={tabId(s.id)} tabIndex={0} hidden={aktiv !== s.id} className="pt-4 focus:outline-hidden">
          {s.inhalt}
        </div>
      ))}
    </div>
  );
}
